import { Fragment, useEffect, useState, type ReactNode } from 'react';
import { Button } from '../ui';

/**
 * The app's standard data grid: a sortable header per column, a filter
 * under each column, row selection, and pagination with a page-size
 * picker. Data is fetched server-side - the grid only reports state
 * changes (`onStateChange`) and renders what it is given, so it works for
 * thousands of rows. Build new list pages on this so every grid behaves
 * the same.
 */

export type GridFilter =
  | { type: 'text'; placeholder?: string }
  | { type: 'select'; options: { value: string; label: string }[] }
  /** Two inputs stored as `${key}Min` / `${key}Max`. */
  | { type: 'range' }
  /** Two date pickers stored as `${key}From` / `${key}To` (YYYY-MM-DD, the user's local days). */
  | { type: 'dateRange' };

export interface GridColumn<T> {
  /** Sort field and filter key. */
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  sortable?: boolean;
  filter?: GridFilter;
  align?: 'left' | 'right';
  width?: number | string;
}

export interface GridState {
  page: number;
  pageSize: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  filters: Record<string, string>;
}

export const PAGE_SIZES = [10, 25, 50, 100];

/** A local calendar day ("2026-10-25", a `dateRange` filter value) -> the instant it starts/ends in the user's timezone. */
export function dayBoundary(day: string | undefined, end: boolean): string | undefined {
  if (!day) return undefined;
  const [y, m, d] = day.split('-').map(Number);
  if (!y || !m || !d) return undefined;
  const at = end ? new Date(y, m - 1, d, 23, 59, 59, 999) : new Date(y, m - 1, d, 0, 0, 0, 0);
  return at.toISOString();
}

/** A numeric filter value, or undefined when empty / not a number. */
export function numberFilter(v: string | undefined): number | undefined {
  return v !== undefined && v.trim() !== '' && !Number.isNaN(Number(v)) ? Number(v) : undefined;
}

/**
 * How one column is filtered/sorted in the browser (`applyClientGrid`):
 * `text` = case-insensitive contains, `equals` = a select filter, `number`
 * = a range filter + numeric sort, `date` = a dateRange filter + date sort.
 * `sort` overrides the sort value.
 */
export interface ClientColumnSpec<T> {
  text?: (row: T) => string | null | undefined;
  equals?: (row: T) => string | null | undefined;
  number?: (row: T) => number | null | undefined;
  date?: (row: T) => string | null | undefined;
  sort?: (row: T) => string | number | null | undefined;
}

/**
 * Filters, sorts and pages an already-loaded list with the same rules as
 * the server-backed grids - for lists that are always small (a tenant's
 * campaigns or automations), so they get the standard grid without new
 * list endpoints. Empty values sort last either way.
 */
export function applyClientGrid<T>(
  rows: T[],
  state: GridState,
  spec: Record<string, ClientColumnSpec<T>>,
): { rows: T[]; total: number } {
  const f = state.filters;
  const filtered = rows.filter((row) =>
    Object.entries(spec).every(([key, s]) => {
      if (s.text && f[key]) {
        const v = s.text(row);
        if (!v || !v.toLowerCase().includes(f[key]!.trim().toLowerCase())) return false;
      }
      if (s.equals && f[key] && s.equals(row) !== f[key]) return false;
      if (s.number) {
        const min = numberFilter(f[`${key}Min`]);
        const max = numberFilter(f[`${key}Max`]);
        const v = s.number(row);
        if ((min !== undefined || max !== undefined) && (v === null || v === undefined))
          return false;
        if (min !== undefined && v! < min) return false;
        if (max !== undefined && v! > max) return false;
      }
      if (s.date) {
        const from = dayBoundary(f[`${key}From`], false);
        const to = dayBoundary(f[`${key}To`], true);
        const v = s.date(row);
        if ((from || to) && !v) return false;
        if (from && new Date(v!) < new Date(from)) return false;
        if (to && new Date(v!) > new Date(to)) return false;
      }
      return true;
    }),
  );

  const s = spec[state.sortBy];
  const sortValue =
    s?.sort ??
    s?.number ??
    (s?.date ? (r: T) => (s.date!(r) ? new Date(s.date!(r)!).getTime() : null) : undefined) ??
    s?.text ??
    s?.equals;
  const sorted = sortValue
    ? [...filtered].sort((a, b) => {
        const va = sortValue(a);
        const vb = sortValue(b);
        const aEmpty = va === null || va === undefined || va === '';
        const bEmpty = vb === null || vb === undefined || vb === '';
        if (aEmpty || bEmpty) return aEmpty === bEmpty ? 0 : aEmpty ? 1 : -1;
        const cmp =
          typeof va === 'number' && typeof vb === 'number'
            ? va - vb
            : String(va).localeCompare(String(vb), undefined, { sensitivity: 'base' });
        return state.sortOrder === 'asc' ? cmp : -cmp;
      })
    : filtered;

  const start = (state.page - 1) * state.pageSize;
  return { rows: sorted.slice(start, start + state.pageSize), total: sorted.length };
}

/** Grid state with the usual rules: a new filter or sort goes back to page 1. */
export function useGridState(initial: Partial<GridState> & Pick<GridState, 'sortBy'>) {
  const [state, setState] = useState<GridState>({
    page: 1,
    pageSize: 25,
    sortOrder: 'asc',
    filters: {},
    ...initial,
  });
  return [state, setState] as const;
}

/** Applies a text filter only after typing pauses, so each key press doesn't query. */
function DebouncedInput({
  value,
  onCommit,
  placeholder,
  label,
  inputMode,
}: {
  value: string;
  onCommit: (v: string) => void;
  placeholder?: string;
  label: string;
  inputMode?: 'decimal';
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  useEffect(() => {
    if (draft === value) return;
    const t = setTimeout(() => onCommit(draft), 300);
    return () => clearTimeout(t);
  }, [draft, value, onCommit]);
  return (
    <input
      className="ne-input ne-grid__filter"
      aria-label={label}
      placeholder={placeholder}
      inputMode={inputMode}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
    />
  );
}

export function DataGrid<T>({
  columns,
  rows,
  rowKey,
  rowLabel,
  total,
  state,
  onStateChange,
  loading = false,
  emptyMessage = 'Nothing matches these filters.',
  selection,
  renderExpanded,
}: {
  columns: GridColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  /** Accessible name for a row (its checkbox reads "Select <label>"). */
  rowLabel?: (row: T) => string;
  total: number;
  state: GridState;
  onStateChange: (next: GridState) => void;
  loading?: boolean;
  emptyMessage?: string;
  /** Row checkboxes; `selectable` false disables a row's box. Selection survives paging. */
  selection?: {
    selected: Set<string>;
    onChange: (next: Set<string>) => void;
    selectable?: (row: T) => boolean;
  };
  /** Content for a full-width row under `row` (e.g. a details sub-grid); null = none. */
  renderExpanded?: (row: T) => ReactNode | null;
}) {
  const setFilter = (key: string, value: string) => {
    const filters = { ...state.filters };
    if (value.trim() === '') delete filters[key];
    else filters[key] = value;
    onStateChange({ ...state, filters, page: 1 });
  };
  const toggleSort = (key: string) =>
    onStateChange({
      ...state,
      sortBy: key,
      sortOrder: state.sortBy === key && state.sortOrder === 'asc' ? 'desc' : 'asc',
      page: 1,
    });

  const pages = Math.max(1, Math.ceil(total / state.pageSize));
  const from = total === 0 ? 0 : (state.page - 1) * state.pageSize + 1;
  const to = Math.min(state.page * state.pageSize, total);
  const goTo = (page: number) => onStateChange({ ...state, page });

  const selectableRows = selection
    ? rows.filter((r) => (selection.selectable ? selection.selectable(r) : true))
    : [];
  const allOnPageSelected =
    selectableRows.length > 0 && selectableRows.every((r) => selection!.selected.has(rowKey(r)));
  const hasFilters = Object.keys(state.filters).length > 0;

  return (
    <div className="ne-grid">
      <div className="ne-grid__scroll">
        <table className="ne-table">
          <thead>
            <tr>
              {selection ? (
                <th style={{ width: 36 }}>
                  <input
                    type="checkbox"
                    aria-label="Select all on this page"
                    disabled={selectableRows.length === 0}
                    checked={allOnPageSelected}
                    onChange={(e) => {
                      const next = new Set(selection.selected);
                      for (const r of selectableRows) {
                        if (e.target.checked) next.add(rowKey(r));
                        else next.delete(rowKey(r));
                      }
                      selection.onChange(next);
                    }}
                  />
                </th>
              ) : null}
              {columns.map((c) => {
                const active = state.sortBy === c.key;
                return (
                  <th
                    key={c.key}
                    style={{ width: c.width, textAlign: c.align }}
                    aria-sort={
                      active ? (state.sortOrder === 'asc' ? 'ascending' : 'descending') : undefined
                    }
                  >
                    {c.sortable ? (
                      <button
                        type="button"
                        className="ne-grid__sort"
                        onClick={() => toggleSort(c.key)}
                        title={`Sort by ${c.header}`}
                      >
                        {c.header}
                        <span aria-hidden="true" className="ne-grid__sort-icon">
                          {active ? (state.sortOrder === 'asc' ? '▲' : '▼') : '↕'}
                        </span>
                      </button>
                    ) : (
                      c.header
                    )}
                  </th>
                );
              })}
            </tr>
            <tr className="ne-grid__filters">
              {selection ? <th /> : null}
              {columns.map((c) => (
                <th key={c.key}>
                  {c.filter?.type === 'text' ? (
                    <DebouncedInput
                      label={`Filter ${c.header}`}
                      placeholder={c.filter.placeholder ?? 'Filter…'}
                      value={state.filters[c.key] ?? ''}
                      onCommit={(v) => setFilter(c.key, v)}
                    />
                  ) : c.filter?.type === 'select' ? (
                    <select
                      className="ne-input ne-grid__filter"
                      aria-label={`Filter ${c.header}`}
                      value={state.filters[c.key] ?? ''}
                      onChange={(e) => setFilter(c.key, e.target.value)}
                    >
                      <option value="">All</option>
                      {c.filter.options.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  ) : c.filter?.type === 'dateRange' ? (
                    <div className="ne-grid__range">
                      <input
                        type="date"
                        className="ne-input ne-grid__filter"
                        aria-label={`${c.header} from`}
                        value={state.filters[`${c.key}From`] ?? ''}
                        onChange={(e) => setFilter(`${c.key}From`, e.target.value)}
                      />
                      <input
                        type="date"
                        className="ne-input ne-grid__filter"
                        aria-label={`${c.header} to`}
                        value={state.filters[`${c.key}To`] ?? ''}
                        onChange={(e) => setFilter(`${c.key}To`, e.target.value)}
                      />
                    </div>
                  ) : c.filter?.type === 'range' ? (
                    <div className="ne-grid__range">
                      <DebouncedInput
                        label={`${c.header} from`}
                        placeholder="Min"
                        inputMode="decimal"
                        value={state.filters[`${c.key}Min`] ?? ''}
                        onCommit={(v) => setFilter(`${c.key}Min`, v)}
                      />
                      <DebouncedInput
                        label={`${c.header} to`}
                        placeholder="Max"
                        inputMode="decimal"
                        value={state.filters[`${c.key}Max`] ?? ''}
                        onCommit={(v) => setFilter(`${c.key}Max`, v)}
                      />
                    </div>
                  ) : null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody aria-busy={loading}>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (selection ? 1 : 0)}
                  style={{ textAlign: 'center', color: 'var(--color-text-muted)' }}
                >
                  {loading ? 'Loading…' : emptyMessage}
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const id = rowKey(row);
                const canSelect = selection?.selectable ? selection.selectable(row) : true;
                const expanded = renderExpanded ? renderExpanded(row) : null;
                return (
                  <Fragment key={id}>
                    <tr style={{ cursor: 'default', opacity: loading ? 0.6 : 1 }}>
                      {selection ? (
                        <td>
                          <input
                            type="checkbox"
                            aria-label={`Select ${rowLabel ? rowLabel(row) : id}`}
                            disabled={!canSelect}
                            checked={selection.selected.has(id)}
                            onChange={() => {
                              const next = new Set(selection.selected);
                              if (next.has(id)) next.delete(id);
                              else next.add(id);
                              selection.onChange(next);
                            }}
                          />
                        </td>
                      ) : null}
                      {columns.map((c) => (
                        <td key={c.key} style={{ textAlign: c.align }}>
                          {c.render(row)}
                        </td>
                      ))}
                    </tr>
                    {expanded ? (
                      <tr style={{ cursor: 'default' }}>
                        <td
                          colSpan={columns.length + (selection ? 1 : 0)}
                          style={{ background: 'var(--color-surface-muted)' }}
                        >
                          {expanded}
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="ne-grid__pager">
        <label className="ne-grid__page-size">
          Rows per page
          <select
            className="ne-input"
            value={state.pageSize}
            onChange={(e) => onStateChange({ ...state, pageSize: Number(e.target.value), page: 1 })}
          >
            {PAGE_SIZES.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <span className="ne-grid__range-text">
          {from}–{to} of {total}
          {hasFilters ? ' (filtered)' : ''}
        </span>
        {hasFilters ? (
          <Button
            variant="secondary"
            onClick={() => onStateChange({ ...state, filters: {}, page: 1 })}
          >
            Clear filters
          </Button>
        ) : null}
        <span style={{ flex: 1 }} />
        <Button
          variant="secondary"
          aria-label="First page"
          disabled={state.page <= 1}
          onClick={() => goTo(1)}
        >
          «
        </Button>
        <Button
          variant="secondary"
          aria-label="Previous page"
          disabled={state.page <= 1}
          onClick={() => goTo(state.page - 1)}
        >
          ‹ Prev
        </Button>
        <span>
          Page {Math.min(state.page, pages)} of {pages}
        </span>
        <Button
          variant="secondary"
          aria-label="Next page"
          disabled={state.page >= pages}
          onClick={() => goTo(state.page + 1)}
        >
          Next ›
        </Button>
        <Button
          variant="secondary"
          aria-label="Last page"
          disabled={state.page >= pages}
          onClick={() => goTo(pages)}
        >
          »
        </Button>
      </div>
    </div>
  );
}
